import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Chip,
  Container,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BarChartIcon from '@mui/icons-material/BarChart';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../context/NotificationsContext';
import { getDashboardStats, getReports } from '../services/reportService';
import { getBrandById } from '../services/brandService';
import { BRAND } from '../theme/brandTheme';
import { getBrandAccent, brandAccentTokens } from '../utils/brandAccent';
import { CroKpiCards, ReportsSection, BrandPageSkeleton } from '../components/dashboard';
import showNotification from '../components/common/Notification';
import '../components/dashboard/ReportListing.css';

/** Single-brand workspace — reports, KPIs and filters scoped to one brand */
const BrandPage = () => {
  const { brandId } = useParams();
  const { user } = useAuth();
  const { lastReportEvent } = useNotifications();

  const [brand, setBrand] = useState(null);
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accessDenied, setAccessDenied] = useState(false);
  const lastSocketEventRef = useRef(null);

  const userRole = user?.role;
  const userBrandIds = useMemo(
    () => user?.brands?.map((b) => b._id) || [],
    [user?.brands],
  );

  const canAccessBrand = useCallback(() => {
    if (!user || !brandId) return false;
    if (userRole === 'admin' || userRole === 'editor') return true;
    return userBrandIds.includes(brandId);
  }, [user, brandId, userRole, userBrandIds]);

  const sortBrandReports = (reportsData) =>
    (reportsData || []).map((b) => ({
      ...b,
      tests: [...(b.tests || [])].sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
      ),
    }));

  const fetchData = useCallback(
    async ({ silent = false } = {}) => {
      if (!canAccessBrand()) {
        setAccessDenied(true);
        setLoading(false);
        return;
      }

      try {
        if (!silent) setLoading(true);
        const [brandData, statsData, reportsData] = await Promise.all([
          getBrandById(brandId),
          getDashboardStats([], brandId),
          getReports([], brandId),
        ]);
        setBrand(brandData);
        setStats(statsData);
        setReports(sortBrandReports(reportsData));
        setError(null);
        setAccessDenied(false);
      } catch (err) {
        console.error('Brand page fetch error:', err);
        if (err.response?.status === 403) {
          setAccessDenied(true);
        } else if (err.response?.status === 404) {
          setError('Marka bulunamadı.');
        } else if (!silent) {
          setError('Veriler yüklenirken bir hata oluştu.');
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [brandId, canAccessBrand],
  );

  useEffect(() => {
    if (lastReportEvent?.at) lastSocketEventRef.current = lastReportEvent.at;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user) return;
    fetchData();
  }, [user, brandId, fetchData]);

  useEffect(() => {
    if (!lastReportEvent) return;
    if (lastSocketEventRef.current === lastReportEvent.at) return;
    lastSocketEventRef.current = lastReportEvent.at;
    fetchData({ silent: true });
    showNotification('Marka verileri güncellendi', 'info');
  }, [lastReportEvent, fetchData]);

  if (loading) {
    return <BrandPageSkeleton />;
  }

  if (accessDenied) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h6" color="error" sx={{ mt: 4 }}>
          Bu markaya erişim yetkiniz bulunmamaktadır.
        </Typography>
        <Button component={RouterLink} to="/dashboard" sx={{ mt: 2 }}>
          Dashboard&apos;a Dön
        </Button>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg">
        <Typography color="error" sx={{ mt: 4, mb: 2 }}>
          {error}
        </Typography>
        <Button variant="contained" onClick={() => fetchData()}>
          Yeniden Dene
        </Button>
      </Container>
    );
  }

  const brandGroup = reports[0];
  const brandStats = brandGroup?.stats || { taslak: 0, durduruldu: 0, canli: 0, toplam: 0 };
  const accent = getBrandAccent(brand, BRAND.purple);
  const tokens = brandAccentTokens(accent);

  return (
    <Container maxWidth="lg">
      <Button
        component={RouterLink}
        to="/dashboard"
        startIcon={<ArrowBackIcon />}
        sx={{ mb: 2, textTransform: 'none', color: accent, fontWeight: 600 }}
      >
        Dashboard
      </Button>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 2,
          mb: 3,
          p: 3,
          borderRadius: 2,
          bgcolor: tokens.softBg,
          border: `1px solid ${tokens.softBorder}`,
          borderLeft: `4px solid ${accent}`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {brand?.logo && (
            <Box
              component="img"
              src={brand.logo}
              alt=""
              sx={{ width: 56, height: 56, objectFit: 'contain' }}
            />
          )}
          <Box>
            <Chip
              label="Marka"
              size="small"
              sx={{ mb: 0.5, bgcolor: tokens.softBg, color: accent, fontWeight: 600, border: `1px solid ${tokens.softBorder}` }}
            />
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 700, color: accent, fontFamily: BRAND.fontFamily }}
            >
              {brand?.name || brandGroup?.name}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
          <HeaderStat label="Canlı" value={brandStats.canli} accent={accent} />
          <HeaderStat label="Taslak" value={brandStats.taslak} accent={accent} />
          <HeaderStat label="Durduruldu" value={brandStats.durduruldu} accent={accent} />
          <HeaderStat label="Toplam" value={brandStats.toplam} accent={accent} />
        </Box>
      </Box>

      <CroKpiCards croKpis={stats?.croKpis} user={user} hideLeaderboard />

      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          component={RouterLink}
          to={`/dashboard/statistics?brand=${brandId}`}
          variant="outlined"
          size="small"
          startIcon={<BarChartIcon />}
          sx={{ borderColor: accent, color: accent, textTransform: 'none' }}
        >
          Marka İstatistikleri
        </Button>
      </Box>

      <ReportsSection
        reports={reports}
        setReports={setReports}
        user={user}
        hideBrandHeader
        expandAllTests
        accentColor={accent}
      />
    </Container>
  );
};

const HeaderStat = ({ label, value, accent }) => (
  <Box sx={{ textAlign: 'center', minWidth: 72 }}>
    <Typography variant="h5" sx={{ fontWeight: 700, color: accent || BRAND.purple }}>
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
  </Box>
);

export default BrandPage;
